// Per-account FINANCIAL-VIEW state — the ADR-0054 finance bindings, projected
// onto the BC dashboard responses.
//
// TikTok grants FINANCE access per TikTok USER, so an account can be perfectly
// reachable (some authorized slot can read its profile) while NO slot can read
// its wallet/budget balance. That used to look like an unexplained blank in the
// Balance column. The backend now answers, on EVERY dashboard account row and
// on the single-account detail:
//
//   "finance_readable": <bool>     — ALWAYS present
//   "finance_slots":    <[]int>    — ALWAYS an array ([] never null)
//
// derived server-side from the SAME source as /admin/tiktok/coverage's
// balance_slots: the ACTIVE slots with a recorded source='finance' binding for
// that advertiser id. finance_readable is simply len(finance_slots) > 0.
//
// OLD-BACKEND TOLERANCE (the reason this module exists rather than two inline
// booleans): during a deploy-skew window the previous build omits both fields.
// Defaulting a MISSING field to false would paint every account "no fin view"
// and send an operator hunting a permission problem that does not exist. So a
// missing field is UNKNOWN (`known: false`) and every consumer renders NOTHING
// instead of a verdict.
//
// SECURITY: these are server-derived booleans/slot NUMBERS only. A slot number
// is an index into the server-side token registry — never a token, never a
// credential (invariant 1). Nothing here is client-asserted: the browser only
// displays what the backend computed.

// The two fields, as they ride on a dashboard account row / detail body. Both
// optional ONLY to model the older backend that omits them.
export interface FinanceViewFields {
  finance_readable?: boolean;
  finance_slots?: number[];
}

// The resolved, render-ready verdict.
export interface FinanceView {
  // false = this backend does not report finance visibility at all. Render
  // nothing (see the deploy-skew note above) — NOT "no fin view".
  known: boolean;
  // At least one authorized identity can read this account's wallet/budget
  // balance. Meaningless when known === false.
  readable: boolean;
  // The slots that carry the finance binding ([] when none / unknown).
  slots: number[];
}

// Resolve the verdict from whatever the backend actually sent. The flag wins
// when present; a backend that sent only the slot array still gets a sensible
// answer (readable = the array is non-empty — exactly the server-side rule).
export function financeView(
  account: FinanceViewFields | null | undefined,
): FinanceView {
  const rawSlots = account?.finance_slots;
  const slotsKnown = Array.isArray(rawSlots);
  const slots = slotsKnown ? rawSlots : [];
  const flagKnown = typeof account?.finance_readable === "boolean";
  return {
    known: flagKnown || slotsKnown,
    readable: flagKnown ? account!.finance_readable === true : slots.length > 0,
    slots,
  };
}

// True for a row an operator should chase: the backend DID report finance
// visibility and it is missing. An unknown row is never counted or filtered —
// that is the deploy-skew guard.
export function lacksFinanceView(
  account: FinanceViewFields | null | undefined,
): boolean {
  const view = financeView(account);
  return view.known && !view.readable;
}

// "slot(s) 2" / "slot(s) 1, 3". Falls back to a non-numeric phrase for the
// (contract-impossible) readable-but-empty-array case rather than printing an
// empty list.
export function financeSlotsLabel(slots: number[]): string {
  return slots.length > 0
    ? `slot(s) ${slots.join(", ")}`
    : "an authorized slot";
}

// The muted-badge / detail-line copy for an account no identity can read.
export const NO_FINANCE_VIEW_TEXT =
  "none — owning BC needs a Finance role";

// Hover copy for the compact badge (the badge itself is deliberately two
// words wide so it fits inside a table cell).
export function financeViewTitle(view: FinanceView): string {
  return view.readable
    ? `Financial view via ${financeSlotsLabel(view.slots)} — wallet/budget balance readable`
    : "No authorized identity can read this account's wallet/budget balance — its owning BC needs a Finance role";
}
